import { useEffect, useLayoutEffect, useRef } from 'react';

const COMMIT_PX = 6;
export const CLOSE_THRESHOLD = 0.33;
const VELOCITY_THRESHOLD = 0.5; // px/ms
// A flick shorter than this is a sloppy tap, not a swipe; the browser still clicks for it.
const MIN_FLICK_PX = 30;

type SwipeRightOptions = {
  enabled: boolean;
  /** Which way the drag goes; `left` for closing a panel that sits on the left. Default `right`. */
  direction?: 'right' | 'left';
  /** The element that follows the finger for a touch starting on `target`, or null to leave it. */
  surfaceFor: (target: Element) => HTMLElement | null;
  /** The drag became horizontal and the surface started to move. */
  onStart?: (surface: HTMLElement) => void;
  /** The surface moved `deltaX` px from where it started, in `direction` (never negative). */
  onMove?: (surface: HTMLElement, deltaX: number) => void;
  /** The finger let go; `passed` when it travelled a third of the surface or flicked. */
  onRelease: (surface: HTMLElement, passed: boolean) => void;
};

// Something under the finger handles horizontal drags itself: a form field, a surface that
// turned panning off (Leaflet map, sortable handle, radial slider), or a horizontal scroller
// that can still scroll the way the finger is going (back to the left on a swipe right, on to
// the right on a swipe left).
const ownsHorizontalDrag = (
  target: Element,
  surface: HTMLElement,
  direction: 'right' | 'left',
): boolean => {
  for (let el: Element | null = target; el && el !== surface; el = el.parentElement) {
    if (el.matches('input, textarea, select, [contenteditable]')) return true;
    const style = getComputedStyle(el);
    if (['none', 'pinch-zoom'].includes(style.touchAction)) return true;
    if (direction === 'right' ? el.scrollLeft > 0 : canScrollOnRight(el, style)) return true;
  }
  return false;
};

const canScrollOnRight = (el: Element, style: CSSStyleDeclaration) =>
  ['auto', 'scroll'].includes(style.overflowX) &&
  el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

/**
 * A touch drag to the right (or, with `direction: 'left'`, to the left) that the page owns from wherever it starts: buttons, cards,
 * text and nested scrollers included. Vertical drags stay scrolls. Used for swipe-back on
 * the page and swipe-to-close on side panels.
 *
 * The app sets `touch-action: pan-x pan-y` on every element (src/index.css), so the browser
 * would take a horizontal drag as a native pan and fire `pointercancel`. Cancelling
 * `touchmove` once the drag is ours keeps it. The listener exists before the touch starts
 * because iOS WebKit decides at `touchstart` whether `touchmove` can be cancelled at all.
 */
export const useSwipeRight = ({ enabled, direction = 'right', ...handlers }: SwipeRightOptions) => {
  const handlersRef = useRef(handlers);
  useLayoutEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!enabled) return;
    const sign = direction === 'left' ? -1 : 1;

    let surface: HTMLElement | null = null;
    let pointerId = -1;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let committed = false;
    // Touch events keep going to the element the touch started on even once it is gone from
    // the page (a loading placeholder replaced mid-drag), so they no longer reach `document`.
    let touched: EventTarget | null = null;

    // Until the drag is ours nothing is cancelled, so a scroll with a little sideways jitter
    // still scrolls.
    const onTouchMove = (e: Event) => {
      if (committed && e.cancelable) e.preventDefault();
    };

    const release = (passed: boolean) => {
      const el = surface;
      const wasCommitted = committed;
      surface = null;
      committed = false;
      touched?.removeEventListener('touchmove', onTouchMove);
      touched = null;
      if (el && wasCommitted) handlersRef.current.onRelease(el, passed);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (surface || e.pointerType !== 'touch' || !e.isPrimary) return;
      if (!(e.target instanceof Element)) return;
      const el = handlersRef.current.surfaceFor(e.target);
      if (!el || ownsHorizontalDrag(e.target, el, direction)) return;
      surface = el;
      touched = e.target;
      touched.addEventListener('touchmove', onTouchMove, { passive: false });
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startTime = e.timeStamp;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!surface || e.pointerId !== pointerId) return;
      const deltaX = (e.clientX - startX) * sign;
      if (!committed) {
        const deltaY = Math.abs(e.clientY - startY);
        if (Math.abs(deltaX) < COMMIT_PX && deltaY < COMMIT_PX) return;
        if (deltaX <= deltaY) {
          release(false);
          return;
        }
        committed = true;
        surface.style.transition = 'none';
        handlersRef.current.onStart?.(surface);
      }
      const x = Math.max(0, deltaX);
      surface.style.transform = `translateX(${x * sign}px)`;
      handlersRef.current.onMove?.(surface, x);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!surface || e.pointerId !== pointerId) return;
      const deltaX = (e.clientX - startX) * sign;
      const elapsed = e.timeStamp - startTime;
      const velocity = elapsed > 0 ? deltaX / elapsed : 0;
      release(
        deltaX >= surface.clientWidth * CLOSE_THRESHOLD ||
          (velocity >= VELOCITY_THRESHOLD && deltaX >= MIN_FLICK_PX),
      );
    };

    const onPointerCancel = (e: PointerEvent) => {
      if (e.pointerId === pointerId) release(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('touchmove', onTouchMove);
      release(false);
    };
  }, [enabled, direction]);
};
